import {
  CubeReducerState,
  Phase,
  Discount,
  Minimum,
  CreditGrant,
  CubeEditorData
} from 'modules/Cube/domain/cube.domain.types'
import { LoadedBillingScheduleData } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/useLoadBillingScheduleEditor'

import { apiDatesAdapters } from '@sequencehq/utils/dates'

import deepmerge from 'deepmerge'
// The `Integrations` index imports Cube, so we go straight to the file here.
import { findNetsuiteItemId } from 'Integrations/integrationsConfig/netsuite/NetsuiteItemSelect/netsuiteItem.constants'
import { scheduleStatusAdapter } from 'modules/Cube/utils/status.adapter'
import { INITIAL_CUBE_STATE } from 'modules/Cube/domain/cube.constants'
import { sortArrayByStartDate } from 'modules/Cube/communication/external/billingSchedule.api.v1/utils/sortArrayByStartDate'
import {
  arrayToIdKeyedObject,
  parseBillingSchedulePhaseDuration
} from '@sequencehq/utils'

export const loadBillingScheduleFromPhases = (
  billingScheduleData: Pick<
    LoadedBillingScheduleData,
    'billingSchedule' | 'billingScheduleSettings' | 'prices'
  >,
  // With a feature off, its data is dropped at the boundary so the editor
  // never sees (and so can never re-emit) it.
  {
    creditGrantsEditingEnabled,
    contractReferenceEditingEnabled
  }: {
    creditGrantsEditingEnabled: boolean
    contractReferenceEditingEnabled: boolean
  }
): {
  common: CubeEditorData['common']
  schedule: CubeEditorData['schedule']
  phases: CubeEditorData['phases']
  minimums: Record<Minimum['id'], Minimum>
  discounts: Record<Discount['id'], Discount>
  creditGrants: Record<CreditGrant['id'], CreditGrant>
} => {
  const { phases, minimums, allPhaseDiscounts, allCreditGrants } =
    billingScheduleData.billingSchedule.phases.reduce(
      (acc, phase) => {
        const minimumsInPhase = (phase.minimums ?? []).map(minimum => ({
          id: crypto.randomUUID(),
          name: '',
          value: `${minimum.amount}`,
          scope: {
            target: minimum.restrictToPrices?.length ? 'specific' : 'allUsage',
            priceIds: minimum.restrictToPrices
          }
        })) as Minimum[]

        const phaseDiscounts = (phase.discounts ?? []).map(discount => ({
          id: crypto.randomUUID(),
          amount: discount.amount,
          message: discount.message,
          priceIds: discount.restrictToPrices,
          discountCalculationType: discount.type,
          duration: 'FOREVER',
          applyToAllPrices: !discount.restrictToPrices.length,
          seatDiscountType: discount.seatDiscountType
        })) as Discount[]

        // Grants are embedded in the phase (like minimums/discounts), read back
        // from the schedule GET. A persisted grant keeps its id for relink on
        // save; empty restrictToPrices means "all prices in the phase".
        const creditGrantsInPhase = (
          creditGrantsEditingEnabled ? (phase.creditGrants ?? []) : []
        ).reduce<CreditGrant[]>((grants, grant) => {
          if (!grant.id) {
            return grants
          }
          return [
            ...grants,
            {
              id: grant.id,
              name: grant.name,
              creditUnitType: grant.creditUnitType,
              currency: grant.currency,
              metricId: grant.metricId,
              amount: grant.amount,
              costOfCredit: grant.costOfCredit,
              taxRateId: grant.taxRateId,
              grantRefreshFrequency: grant.grantRefreshFrequency,
              billingFrequency: grant.billingFrequency,
              rolloverMode: grant.rolloverMode,
              priceIds: grant.restrictToPrices ?? [],
              netsuiteItemId: findNetsuiteItemId(grant.integrationIds)
            }
          ]
        }, [])

        return deepmerge(acc, {
          phases: [
            {
              id: phase.id,
              name: phase.name ?? '',
              externalContractRef: phase.externalContractRef,
              contractReferences: contractReferenceEditingEnabled
                ? (phase.contractReferences ?? [])
                : undefined,
              startDate: apiDatesAdapters.fromApi(phase.startDate),
              endDate: phase.endDate
                ? apiDatesAdapters.fromApi(phase.endDate)
                : undefined,
              minimumIds: minimumsInPhase.map(({ id }) => id),
              priceIds: phase.priceIds,
              discountIds: phaseDiscounts.map(({ id }) => id),
              creditGrantIds: creditGrantsInPhase.map(({ id }) => id),
              recurrencePreference: phase.recurrencePreference,
              phasePriceMetadata: phase.phasePriceMetadata
            }
          ],
          minimums: minimumsInPhase,
          allPhaseDiscounts: phaseDiscounts,
          allCreditGrants: creditGrantsInPhase
        })
      },
      {} as {
        phases: (Omit<Phase, 'duration'> & {
          startDate: Date | undefined
          endDate: Date | undefined
        })[]
        minimums: Minimum[]
        allPhaseDiscounts: Discount[]
        allCreditGrants: CreditGrant[]
      }
    )

  const sortedPhases: Array<Phase> = sortArrayByStartDate<
    Omit<Phase, 'duration'> & {
      startDate: Date | undefined
      endDate: Date | undefined
    }
  >(phases).map(({ startDate, endDate, ...rest }) => ({
    ...rest,
    duration: parseBillingSchedulePhaseDuration({ startDate, endDate })
  }))

  const currency = billingScheduleData.prices[0]?.currency ?? undefined

  return {
    common: {
      id: billingScheduleData.billingSchedule.id,
      startDate: apiDatesAdapters.fromApi(
        billingScheduleData.billingSchedule.startDate
      ),
      status: scheduleStatusAdapter.in(
        billingScheduleData.billingSchedule.status
      ),
      currency,
      title: '',
      alias: '',
      customerId: billingScheduleData.billingSchedule.customerId,
      isArchived: Boolean(billingScheduleData.billingSchedule.archivedAt),
      phaseIds: sortedPhases.map(({ id }) => id),
      createdAt: new Date(billingScheduleData.billingSchedule.createdAt),
      lockedByQuoteId: billingScheduleData.billingSchedule.lockedByQuoteId
    },
    schedule: {
      stripePayment:
        billingScheduleData.billingScheduleSettings?.paymentProvider ===
        'STRIPE',
      stripeAutoCharge: Boolean(
        billingScheduleData.billingScheduleSettings?.autoCharge
      ),
      goCardlessPayment:
        billingScheduleData.billingScheduleSettings?.paymentProvider ===
        'GOCARDLESS',
      recurrenceDayOfMonth:
        billingScheduleData.billingSchedule.recurrenceDayOfMonth ?? 1,
      purchaseOrderNumber:
        billingScheduleData.billingSchedule.purchaseOrderNumber ?? '',
      reference: billingScheduleData.billingSchedule.reference ?? '',
      label: billingScheduleData.billingSchedule.label ?? '',
      autoIssueInvoices:
        billingScheduleData.billingSchedule.autoIssueInvoices ?? false,
      taxRateId:
        billingScheduleData.billingSchedule.taxRates?.[0]?.taxRateId ?? '',
      rollUpBilling: billingScheduleData.billingSchedule.rollUpBilling ?? false,
      attachmentAssets:
        billingScheduleData.billingSchedule.attachmentAssets ?? [],
      defaultDueDateDays:
        billingScheduleData.billingSchedule.defaultDueDateDays ?? null,
      memo: billingScheduleData.billingSchedule.memo ?? '',
      customFields: Object.entries(
        billingScheduleData.billingSchedule.customFields ?? {}
      ).map(([key, value]) => ({
        // Server returns a flat key/value map. We mint a stable client-side
        // id so the editor can track rows across re-renders independently
        // of the (mutable) key.
        id: `existing-${key}`,
        key,
        value
      })),
      replaceContractReferences: false
    },
    phases: arrayToIdKeyedObject(sortedPhases),
    minimums: arrayToIdKeyedObject(minimums),
    discounts: arrayToIdKeyedObject(allPhaseDiscounts),
    creditGrants: arrayToIdKeyedObject(allCreditGrants)
  }
}

/**
 * A collection of loader/transformers to create loadAction data from a variety of sources (e.g.
 * different Api versions, imports, or potentially even quotes in the future)
 * @param billingScheduleData
 * @returns
 */
export const phasesAdapterIn = (
  billingScheduleData: LoadedBillingScheduleData,
  configuration: CubeReducerState['configuration']
): Pick<CubeReducerState, 'data' | 'configuration'> => {
  const transformedBillingScheduleData = loadBillingScheduleFromPhases(
    billingScheduleData,
    {
      creditGrantsEditingEnabled: configuration.features.creditGrantsEditing,
      contractReferenceEditingEnabled:
        configuration.features.contractReferenceEditing
    }
  )

  return {
    data: {
      ...transformedBillingScheduleData,
      quote: INITIAL_CUBE_STATE.data.quote,
      presentation: INITIAL_CUBE_STATE.data.presentation,
      customers: arrayToIdKeyedObject(billingScheduleData.customers),
      taxRates: arrayToIdKeyedObject(billingScheduleData.taxRates),
      products: arrayToIdKeyedObject(billingScheduleData.products),
      prices: arrayToIdKeyedObject(billingScheduleData.prices),
      merchantBranding: INITIAL_CUBE_STATE.data.merchantBranding
    },
    configuration: {
      ...configuration,
      currency: {
        default: billingScheduleData.enabledCurrencies[0],
        enabled: billingScheduleData.enabledCurrencies
      },
      /**
       * An account that has never opened the settings page has no stored value
       * for these, so each falls back to the base configuration's default.
       */
      editorDefaults: {
        phaseEditingMode:
          billingScheduleData.billingSettings?.phaseEditingMode ??
          configuration.editorDefaults.phaseEditingMode,
        resetBillingPeriod:
          billingScheduleData.billingSettings?.initialPhaseResetBillingPeriod ??
          configuration.editorDefaults.resetBillingPeriod
      }
    }
  }
}
